import { Link } from "react-router-dom";
import { PUBLIC_HOST } from "../lib/domain";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background" data-testid="privacy-policy-page">
      <header className="hard-border-b px-8 py-6">
        <Link to="/" className="ticker text-muted-foreground hover:text-foreground" data-testid="back-home">← Mon Assistant Pro</Link>
      </header>
      <main className="max-w-3xl mx-auto px-8 py-16 prose-style">
        <h1 className="font-display font-black text-5xl tracking-tight mb-2">Politique de confidentialité</h1>
        <div className="ticker text-muted-foreground mb-12">Dernière mise à jour : 29 avril 2026</div>

        <section className="space-y-3 mb-10">
          <p className="text-base">
            <strong>DOJOVA</strong> (également connue sous les noms « Kaizen System » et « Mon Assistant Pro »,
            ci-après « l'<strong>Application Kaizen</strong> » ou « l'Application »)
            est un outil de gestion d'entreprise opéré par
            <strong> l'Institut Impact Élite Martial / Kaizen System Inc.</strong> (Kru Ralph D. Jean-Pierre, Val-d'Or, Québec, Canada),
            accessible via {PUBLIC_HOST}.
            Cette politique décrit quelles données l'application Kaizen collecte, comment elles sont utilisées,
            et les droits dont disposent les utilisateurs en vertu du <em>Règlement général sur la protection des données</em> (RGPD),
            de la <em>Loi canadienne sur la protection des renseignements personnels et les documents électroniques</em> (LPRPDE),
            et de la <em>Loi 25 du Québec</em>.
          </p>
          <p className="text-base">
            L'application Kaizen intègre, lorsque l'utilisateur l'autorise explicitement, des services tiers incluant
            <strong> TikTok</strong>, Facebook, Instagram, YouTube, Stripe, Twilio, Printful et QuickBooks.
            Toutes ces intégrations respectent strictement les conditions d'utilisation et politiques de confidentialité
            de chaque plateforme, incluant les <em>TikTok Developer Terms of Service</em>.
          </p>
        </section>

        <Section n="01" title="Données collectées">
          <ul>
            <li><strong>Compte</strong> : courriel, nom, mot de passe (haché bcrypt), informations professionnelles facultatives.</li>
            <li><strong>Contenu opérationnel</strong> : transactions financières, dossiers d'athlètes (avec consentement), brouillons de courriels et de campagnes, articles de blog, messages de contact.</li>
            <li><strong>Connexions tierces</strong> : tokens OAuth chiffrés (Meta, Google, etc.) — uniquement après autorisation explicite via la page <Link to="/app/integrations" className="text-[#D90429] underline">Intégrations</Link>.</li>
            <li><strong>Audit technique</strong> : adresse IP, journaux de connexion, horodatage des actions sensibles.</li>
          </ul>
        </Section>

        <Section n="02" title="Utilisation des données">
          <ul>
            <li>Faire fonctionner les modules (finances, marketing, athlètes, événements).</li>
            <li>Générer du contenu via des modèles d'IA (Claude, Gemini, OpenAI) — vos données ne sont jamais utilisées pour entraîner ces modèles.</li>
            <li>Publier sur vos comptes Facebook / Instagram / LinkedIn quand vous l'autorisez.</li>
            <li>Vous notifier (formulaires de contact, alertes).</li>
          </ul>
          <p className="mt-3"><strong>Aucune donnée n'est revendue à des tiers.</strong></p>
        </Section>

        <Section n="03" title="Données partagées avec Meta (Facebook & Instagram)">
          <p>Si vous connectez votre compte Meta via la page Intégrations :</p>
          <ul>
            <li>Nous stockons votre Page Access Token (non-expirant) pour publier en votre nom.</li>
            <li>Nous lisons l'identifiant de votre Page Facebook et de votre compte Instagram Business associé.</li>
            <li>Nous n'accédons jamais à vos messages privés, à votre liste d'amis ou à votre historique personnel.</li>
            <li>Vous pouvez révoquer cet accès à tout moment depuis votre <a href="https://www.facebook.com/settings?tab=business_tools" target="_blank" rel="noopener noreferrer" className="text-[#D90429] underline">Facebook Business Tools</a> ou depuis la page Intégrations de Mon Assistant Pro.</li>
          </ul>
        </Section>

        <Section n="04" title="Données partagées avec TikTok">
          <p>Si vous connectez votre compte TikTok via la page Intégrations (autorisation OAuth explicite) :</p>
          <ul>
            <li><strong>Profil de base</strong> (<code>user.info.basic</code>) : nous lisons votre <em>open_id</em>, votre nom d'affichage et votre photo de profil, uniquement pour identifier le compte connecté.</li>
            <li><strong>Publication de vidéos</strong> (<code>video.upload</code>, <code>video.publish</code>) : nous téléversons et publions des vidéos <strong>uniquement à votre demande explicite</strong>, sur votre propre compte TikTok.</li>
            <li>Nous stockons votre token d'accès TikTok de façon chiffrée, jamais exposé au front-end.</li>
            <li>Nous n'accédons <strong>jamais</strong> à vos messages privés, votre liste d'abonnés, votre historique de visionnement ni vos données personnelles au-delà du strict nécessaire.</li>
            <li>Nous n'utilisons <strong>pas</strong> les données TikTok à des fins publicitaires et ne les revendons à aucun tiers.</li>
            <li>Vous pouvez révoquer cet accès à tout moment depuis vos <a href="https://www.tiktok.com/setting" target="_blank" rel="noopener noreferrer" className="text-[#D90429] underline">paramètres TikTok</a> ou depuis la page Intégrations de l'application.</li>
            <li>Notre usage des API TikTok respecte les <em>TikTok Developer Terms of Service</em> et les <em>TikTok Community Guidelines</em>.</li>
          </ul>
        </Section>

        <Section n="05" title="Conservation des données">
          <p>Les données sont conservées tant que votre compte est actif. À la suppression du compte, toutes les données sont effacées sous 30 jours, à l'exception des journaux d'audit de sécurité (conservés 12 mois pour conformité légale).</p>
        </Section>

        <Section n="06" title="Sécurité">
          <ul>
            <li>Connexions chiffrées (TLS 1.2+) sur tout le trafic.</li>
            <li>Mots de passe stockés en bcrypt, jamais en clair.</li>
            <li>Tokens d'API stockés dans une base MongoDB protégée, jamais exposés au front-end.</li>
            <li>Accès restreint au seul administrateur (mode solo).</li>
          </ul>
        </Section>

        <Section n="07" title="Vos droits">
          <p>Conformément au RGPD et à la LPRPDE, vous pouvez à tout moment :</p>
          <ul>
            <li>Demander une copie de vos données (<em>droit d'accès</em>).</li>
            <li>Demander leur correction (<em>droit de rectification</em>).</li>
            <li>Demander leur suppression complète : voir <Link to="/data-deletion" className="text-[#D90429] underline">/data-deletion</Link>.</li>
            <li>Retirer votre consentement à tout moment.</li>
          </ul>
        </Section>

        <Section n="08" title="Contact">
          <p>Pour toute question relative à cette politique, à vos données ou à un incident :</p>
          <p className="font-mono text-sm">
            Ralph David Gassonnette Jean-Pierre<br />
            Institut Impact Élite Martial<br />
            Val-d'Or, Québec, Canada<br />
            Courriel : <a href="mailto:impactkb.bjj@gmail.com" className="text-[#D90429] underline">impactkb.bjj@gmail.com</a>
          </p>
        </Section>

        <div className="mt-16 pt-8 border-t border-border text-sm text-muted-foreground">
          Voir aussi : <Link to="/data-deletion" className="text-[#D90429] underline">Suppression des données</Link>
        </div>
      </main>
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section className="mb-10" data-testid={`section-${n}`}>
      <div className="ticker text-muted-foreground mb-1">{n}</div>
      <h2 className="font-display font-bold text-2xl mb-4">{title}</h2>
      <div className="space-y-2 text-base leading-relaxed [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5">
        {children}
      </div>
    </section>
  );
}
